import React, {useEffect, useMemo, useState} from 'react';
import {Spin} from "antd";
import useScreenSize from "../../../helpers/hooks/useScreenSize";
import styled from "styled-components";
import {
    Btn,
    FlexColumnSection, FlexRowSection,
    GreyTextClickable,
    Header,
    Wrapper
} from "../../../styles/globalStyles";
import FormInput from "../../../components/UI/FormInput";
import { Formik } from 'formik';
import * as yup from "yup";
import {useDispatch, useSelector} from "react-redux";
import {login, resetError} from '@arboxappv4/shared/src/redux/authSlice'
import {
    FORGOT_PASSWORD_MODAL, hideModal, LOGIN_MODAL, ONE_TIME_PASSWORD_MODAL,
    openModal, updateModalRes
} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import {useNavigate, useSearchParams} from "react-router-dom";
import {t} from "i18next";
import CustomModal from "../../../components/UI/CustomModal";
import {
    isILPhone,
    onLinkClicked
} from "../../../helpers/functions";
import Logo from "../../../components/Logo";
import {Divider, Button} from "antd";
import {Colors} from "@arboxappv4/shared/src/styles/Colors";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import IconGenerator from "../../../helpers/IconGenerator";
import CustomPhone from "../../../components/UI/CustomPhone";
import { isValidPhoneNumber } from 'react-phone-number-input'
import {HEADER_SIGN_IN_CALLER, iconPrefix} from "@arboxappv4/shared/src/helpers/constants";
import {setSignInProps} from "@arboxappv4/shared/src/redux/siteSlice";
import iconGenerator from "../../../helpers/IconGenerator";
import {updateFlags} from "@arboxappv4/shared/src/redux/stepperSlice";
import {flowOptions} from "../../../flowManager/FlowOptions";
import {HIDE_SCHEDULE_PAGE_SETTINGS, HIDE_SIGNUP_SETTINGS} from "../../../../../src/constants";


const Login = (props) => {
    const { isOpen, onClose, membership, email, caller, mailOnly } = props;
    const error = useSelector(state => state.auth.error)
    const user = useSelector(state => state.user.data)
    const box = useSelector(state => state.site.box)
    const selectedLocation = useSelector(state => state.site.selectedLocation)
    const whitelabelProps = useSelector(state => state.site.whitelabelProps)
    const oneTimePasswordModalRes = useSelector(state => state.modalManager[ONE_TIME_PASSWORD_MODAL].processRes)
    const [authError, setAuthError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isOTPScreen, setIsOTPScreen] = useState(true)
    const [phoneOTPError, setPhoneOTPError] = useState(null);
    const [selectedMessageType, setSelectedMessageType] = useState(null);
    const {isMobile} = useScreenSize();
    const dispatch = useDispatch()
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);

    const allowPhoneVerify = useMemo(() => box?.boxes_settings?.some(setting => setting.prop_name === 'sitePhoneVerify' && setting.properties && setting.properties.active), [box])
    const hasPhoneOTP = useMemo(() => !!whitelabelProps?.properties?.loginOptions || allowPhoneVerify, [whitelabelProps, allowPhoneVerify]);
    const hasEmailLogin = useMemo(() => box?.email_table !== 'users_boxes', [box]);
    const hideSignup = useMemo(() => !!box?.boxes_settings?.find(setting => setting.prop_name === HIDE_SIGNUP_SETTINGS)?.properties?.hide, [box]);

    const validationSchema = useMemo(() => {
        return yup.object().shape({
            email: yup.string().when([], {
                is: () => !isOTPScreen || selectedMessageType === 'email',
                then: () => yup.string().email(t('email-invalid-format')).required(t('email-required-field')),
                otherwise: () => yup.string(),
            }),
            password: yup.string().when([], {
                is: () => isOTPScreen,
                then: () => yup.string(),
                otherwise: () => yup.string().required(t('mandatory-field'))
            }),
            phone: yup.string().when([], {
                is: () => isOTPScreen && selectedMessageType === 'phone',
                then: () => yup.string().required(t('mandatory-field')).test('phone-validator', t('invalid-phone'), (value) => value && isValidPhoneNumber(value) === true),
                otherwise: () => yup.string()
            })
        })
    }, [isOTPScreen, selectedMessageType]);

    const otpOptions = useMemo(() => {
        let options = []
        if(hasPhoneOTP && !mailOnly) {
            options.push({id: 'phone', icon: 'phone', label: t('phone')})
        }
        if (hasEmailLogin) {
            options.push({id: 'email', icon: 'envelope', label: t('email')})
        }
        return options
    }, [hasPhoneOTP, mailOnly, hasEmailLogin]);

    useEffect(() => {
        setAuthError(error ? t(error) : null)
        setLoading(false)
    }, [error]);

    useEffect(() => {
        if(user && !isOTPScreen) { // if logged in with email and password, we don't have a flow res from OneTimePassword, so we need to trigger "by hand"
            if(caller !== HEADER_SIGN_IN_CALLER) {
                continueCallerFlow()
            } else {
                if(window.location.pathname === '/register') onLinkClicked('/membership', navigate)
                else window.location.reload() //if from header - reload page
            }
        }
    }, [user]);

    useEffect(() => {
        if (otpOptions.length >= 1) {
            setSelectedMessageType(otpOptions[0].id)
        }
    }, [otpOptions]);

    useEffect(() => {
        //if user logged in from flow sign in
        if(oneTimePasswordModalRes) {
            dispatch(updateModalRes({modalName: ONE_TIME_PASSWORD_MODAL, res: null}))
            continueCallerFlow()
        }
    }, [oneTimePasswordModalRes]);

    useEffect(() => {
        if(params.leadToken && [flowOptions.MEMBERSHIP_PURCHASE, flowOptions.DIRECT_MEMBERSHIP_PURCHASE].includes(caller)) {
            goToRegister(true)
        }
    }, [params.leadToken, caller]);


    const closeLogin = () => {
        dispatch(resetError(null))
        onClose()
    }

    const openForgotPassword = (email) => {
        closeLogin()
        dispatch(openModal({modalName: FORGOT_PASSWORD_MODAL, props: {email}}))
    }

    const goToRegister = (fromMembershipSignIn = false) => {
        if(hideSignup) {
            setAuthError(t('user-not-found'))
            return
        }
        dispatch(updateFlags({fromMembershipSignIn}))
        onLinkClicked('/register', navigate, {state: {membership: membership}})
        closeLogin()
    }

    const submit = async (values) => {
        setLoading(true)
        if(isOTPScreen) {
            submitOTP(values)
        } else {
            await dispatch(login(values))
        }
    }

    const submitOTP = (values) => {
        dispatch(setSignInProps({phone: values.phone, email: values.email}))
        if(selectedMessageType === 'phone') {
            submitPhoneOTP(values)
        } else if(selectedMessageType === 'email') {
            submitEmailOTP(values)
        }
    }

    const submitPhoneOTP = async (values) => {
        try {
            if(!isILPhone(values.phone)) {
                throw {error: {messageToUser: 'globalPhoneError'}}
            }
            const res = await apiAction(`validatePhoneVerify`, 'post', {phone: values.phone});
            if(!res || res.isLeadInBox) {
                if(caller === HEADER_SIGN_IN_CALLER) {
                    goToRegister()
                    return
                } else {
                    continueCallerFlow()
                    return
                }
            }
            const otpId = await apiAction(`mfa`, 'post', {type: 'sms', value: values.phone, check_format: true});
            dispatch(openModal({
                modalName: ONE_TIME_PASSWORD_MODAL,
                props: {type: 'sms', phone: values.phone, otpId: otpId, caller: caller},
                onClose: () => {
                    dispatch(hideModal({modalName: LOGIN_MODAL, hide: false}))
                    setLoading(false)
                }
            }))
            dispatch(hideModal({modalName: LOGIN_MODAL, hide: true}))
        } catch (e) {
            setLoading(false)
            setPhoneOTPError(t(e?.error?.messageToUser ?? 'phone-otp-error'))
        }
    }

    const submitEmailOTP = async (values) => {
        try {
            await apiAction(`site/checkValidForLogin`, 'post', {email: values.email});
            const otpId = await apiAction(`mfa`, 'post', {type: 'email', value: values.email});
            dispatch(openModal({
                modalName: ONE_TIME_PASSWORD_MODAL,
                props: {email: values.email, otpId: otpId, caller: caller},
                onClose: () => {
                    dispatch(hideModal({modalName: LOGIN_MODAL, hide: false}))
                    setLoading(false)
                }
            }))
            dispatch(hideModal({modalName: LOGIN_MODAL, hide: true}))
        } catch (e) {
            setLoading(false)
            if(['noUser', 'noUserBox', 'userIsLead'].includes(e.error?.messageToUser)) {
                if(caller === HEADER_SIGN_IN_CALLER) {
                    goToRegister()
                } else {
                    continueCallerFlow()
                }
            } else {
                setAuthError(t(e?.error?.messageToUser ?? 'email-otp-error'))
            }
        }
    }

    const continueCallerFlow = () => {
        if(hideSignup) {
            setAuthError(t('user-not-found'))
            return
        }
        dispatch(updateModalRes({modalName: LOGIN_MODAL, res: {continueFlow: caller}}))
        closeLogin()
    }

    return (
        <CustomModal open={isOpen} onCancel={closeLogin} width={isMobile ? 'inherit' : '420px'}>
            <Spin spinning={loading}>
            <Wrapper>
                <Formik initialValues={{ email: email ?? '', password: '', phone: '' }} validationSchema={validationSchema} onSubmit={submit}>
                    {({values, errors, touched, setFieldValue, handleSubmit}) => (
                        <FlexColumnSection gap={'20px'}>
                            {isMobile && <Logo onClick={closeLogin}/>}
                            <FlexColumnSection flex={'none'}>
                                <Header>{t('login')}</Header>
                                <div>{t('login-subheader')}</div>
                            </FlexColumnSection>
                            <FlexColumnSection gap={'20px'}>
                                {isOTPScreen && otpOptions?.length > 1 &&
                                    <FlexRowSection gap={'20px'}>
                                        {otpOptions.map(option =>
                                            <SendViaOption active={selectedMessageType === option.id} onClick={() => setSelectedMessageType(option.id)}>
                                                <FontAwesomeIcon icon={iconGenerator({name: option.icon, type: iconPrefix.REGULAR})} color={Colors.siteBlack}/>
                                                <span>{option.label}</span>
                                            </SendViaOption>
                                        )}
                                    </FlexRowSection>}
                                {isOTPScreen &&
                                    <>
                                        {selectedMessageType === 'phone' && <CustomPhone value={values.phone}
                                                                                         error={(touched.phone && errors.phone) ? errors.phone : phoneOTPError}
                                                                                         onChange={(val) => setFieldValue('phone', val)}
                                                                                         country_code={selectedLocation?.country_code}/>}
                                        {selectedMessageType === 'email' && <FormInput label={t('email')} value={values.email}
                                                                                       error={(touched.email && errors.email) ? errors.email : authError}
                                                                                       onChangeCallback={(val) => setFieldValue('email', val)}
                                                                                       type={'email'} autoComplete={'email'} inputMode={'email'}/>}
                                    </>
                                }
                                {!isOTPScreen &&
                                    <FlexColumnSection gap={'5px'} flex={'none'}>
                                        <FlexColumnSection gap={'15px'}>
                                            <FormInput label={t('email')} value={values.email}
                                                       error={(touched.email && errors.email) ? errors.email : authError}
                                                       onChangeCallback={(val) => setFieldValue('email', val)}
                                                       type={'email'} autoComplete={'email'} inputMode={'email'}/>
                                            <FormInput label={t('password')} isPassword={true} value={values.password}
                                                       error={(touched.password && errors.password) ? errors.password : null}
                                                       onChangeCallback={(val) => setFieldValue('password', val)}/>
                                        </FlexColumnSection>
                                        <GreyTextClickable onClick={() => openForgotPassword(values.email)}>{t('forgot-password')}</GreyTextClickable>
                                    </FlexColumnSection>
                                }
                            </FlexColumnSection>
                            <Btn type={'primary'} onClick={handleSubmit} width={isMobile ? '100%' : null}>{t(isOTPScreen ? 'send-sms-opt' : 'login')}</Btn>
                            {hasEmailLogin && <FlexColumnSection gap={'5px'} flex={'none'}>
                                <Divider plain>{t('or-separator')}</Divider>
                                <PhoneOTPBtn onClick={() => setIsOTPScreen(prev => !prev)}>
                                    <FontAwesomeIcon
                                        icon={IconGenerator({name: isOTPScreen ? 'key' : 'mobile-screen-button'})}
                                        color={Colors.siteBlack}/>
                                    {t(isOTPScreen ? 'email-login' : 'phone-otp-login')}
                                </PhoneOTPBtn>
                            </FlexColumnSection>}
                            <div>
                                <Conditions>{t('agree-to')} </Conditions>
                                <ConditionsLink onClick={() => window.open('https://www.arboxapp.com/terms-and-conditions', '_blank')}>{t('terms-of-Use')}</ConditionsLink>
                                <Conditions> {t('acknowledge-and-read')} </Conditions>
                                <ConditionsLink onClick={() => window.open('https://www.arboxapp.com/privacy-policy', '_blank')}>{t('privacy-policy')}</ConditionsLink>
                            </div>
                        </FlexColumnSection>
                    )}
                </Formik>
            </Wrapper>
            </Spin>
        </CustomModal>
    );
};

export default Login;

const Conditions = styled.span`
    font-size: 12px;
`;

const ConditionsLink = styled(Conditions)`
    text-decoration: underline;
    cursor: pointer;
`;

const PhoneOTPBtn = styled(Button)`
    gap: 15px;
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border-color: ${Colors.siteScheduleBorder};
`;


const SendViaOption = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
    justify-content: center;
    padding: 15px;
    flex: 1;
    border: ${({active}) => `1px solid ${active ? Colors.siteBlack : Colors.siteScheduleBorder}`};
    font-weight: 500;
    border-radius: 5px;
    cursor: pointer;

    :hover {
        border-color: ${Colors.greyText};
    }
`;
